import { Box, Button, HStack, SimpleGrid, Text, useToast } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ChevronLeftIcon, PenIcon } from 'lucide-react';
import { quickReferralModalState } from '../../tenantState/QuickReferralState';
import { useSnapshot } from 'valtio';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { SingleField } from '@duke-hq/ui/components/SingleField/SingleField';
import { djs } from '@duke-hq/djs';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { useCallback, useMemo } from 'react';
import { CourseSegment, WorkSchedule } from '@duke-hq/shared-constants/enrolments';
import { getCourseDatesFromAdminCourseSchedule, toUtcStartOfDay } from '@duke-hq/lib/dates';
import { ParticipantExistsAlert } from './ParticipantExistsAlert';

type SummaryProps = {
  goToPrevStep: () => void;
  setStep: (step: React.SetStateAction<number>) => void;
};

export function Summary({ goToPrevStep, setStep }: SummaryProps) {
  const quickReferralModalSnap = useSnapshot(quickReferralModalState);
  const utils = trpcTenant.useUtils();
  const toast = useToast();
  const { data: course, isLoading } = trpcTenant.courses.courseSchedule.useQuery(
    quickReferralModalSnap.courseData.id,
  );

  const invalidateRelevantQueries = () => {
    void utils.users.listStudentsWithPagination.invalidate();
    void utils.users.snapshot.invalidate();
    void utils.enrolments.getMemberInternalEnrolmentCount.invalidate();
  };

  const createStudent = trpcTenant.users.createStudent.useMutation({
    onSuccess: (user) => {
      const { courseData, enrolmentSettingsData, basicInfoData, existingStudentId } =
        quickReferralModalSnap;
      createCourseEnrolment.mutate({
        user_id: user.id,
        course_id: courseData.id,
        start_at_date: enrolmentSettingsData.start_at_date,
        enrolment_contract_type: enrolmentSettingsData.enrolment_contract_type,
        contract_category: enrolmentSettingsData.contract_category,
        work_schedule: enrolmentSettingsData.work_schedule,
        course_segment: enrolmentSettingsData.course_segment,
        consultant_id: enrolmentSettingsData.selected_consultant.consultant_id,
        site_location_id: enrolmentSettingsData.selected_site.site_id,
        external_id: enrolmentSettingsData.external_id,
        pendingStudentData:
          existingStudentId != null
            ? {
                date_of_birth: basicInfoData.date_of_birth,
                universal_student_identifier: basicInfoData.universal_student_identifier,
              }
            : undefined,
      });
    },
    onError: (error) => {
      toast({
        title: t`Failed to create student.`,
        description: error.message,
        status: 'error',
        duration: 7000,
      });
      invalidateRelevantQueries();
    },
  });

  const isPendingReview =
    quickReferralModalSnap.hasActiveAccreditedCourse &&
    quickReferralModalSnap.courseData.accredited;

  const createCourseEnrolment = trpcTenant.enrolments.createCourseEnrolment.useMutation({
    onSuccess: () => {
      toast({
        title: isPendingReview ? t`Referral submitted for review.` : t`Course enrolment created.`,
        description: isPendingReview ? t`An administrator will review this enrolment.` : undefined,
        status: 'success',
      });
      quickReferralModalState.resetStates();
      quickReferralModalState.closeModal();
    },
    onError: (error) => {
      toast({
        title: isPendingReview
          ? t`Failed to submit referral.`
          : t`Failed to create course enrolment.`,
        description: error.message,
        status: 'error',
        duration: 7000,
      });
    },
    onSettled: () => {
      invalidateRelevantQueries();
    },
  });

  const isSubmitting = createStudent.isPending || createCourseEnrolment.isPending;

  const courseStartAndEndDates = useMemo(() => {
    if (!course || isLoading) {
      return null;
    }

    try {
      return getCourseDatesFromAdminCourseSchedule({
        adminCourseScheduleItems: course.schedule,
        courseSegment:
          quickReferralModalSnap.enrolmentSettingsData.course_segment ?? CourseSegment.FULL_COURSE,
        workSchedule:
          quickReferralModalSnap.enrolmentSettingsData.work_schedule ?? WorkSchedule.FULL_TIME,
        startAt: toUtcStartOfDay(
          quickReferralModalSnap.enrolmentSettingsData.start_at_date,
          'YYYY-MM-DD',
        ),
      });
    } catch {
      return null;
    }
  }, [
    course,
    isLoading,
    quickReferralModalSnap.enrolmentSettingsData.course_segment,
    quickReferralModalSnap.enrolmentSettingsData.start_at_date,
    quickReferralModalSnap.enrolmentSettingsData.work_schedule,
  ]);

  const handleSubmit = () => {
    const { basicInfoData, enrolmentSettingsData } = quickReferralModalSnap;

    createStudent.mutate({
      email: basicInfoData.email,
      phone_number: basicInfoData.phone_number,
      first_name: basicInfoData.first_name,
      last_name: basicInfoData.last_name,
      studentData: {
        date_of_birth: basicInfoData.date_of_birth,
        universal_student_identifier: basicInfoData.universal_student_identifier,
        owned_by: enrolmentSettingsData.selected_consultant.consultant_id,
      },
    });
  };

  const getCoursePrice = useCallback(() => {
    if (
      quickReferralModalSnap.enrolmentSettingsData.course_segment ===
      CourseSegment.FIRST_SEMESTER_ONLY
    ) {
      return parseFloat(quickReferralModalSnap.courseData.price_aud.toString()) / 2;
    }

    return quickReferralModalSnap.courseData.price_aud?.toString() ?? 0;
  }, [
    quickReferralModalSnap.enrolmentSettingsData.course_segment,
    quickReferralModalSnap.courseData.price_aud,
  ]);

  return (
    <FormMainContainer>
      <FormContentContainer>
        <ParticipantExistsAlert />
        <Box w="full">
          <SummarySectionHeading onEditClick={() => setStep(0)}>
            <Trans>Basic Information</Trans>
          </SummarySectionHeading>
          <SimpleGrid columns={2} w="full">
            <SingleField
              name={<Trans>First Name</Trans>}
              value={quickReferralModalSnap.basicInfoData.first_name}
            />
            <SingleField
              name={<Trans>Last Name</Trans>}
              value={quickReferralModalSnap.basicInfoData.last_name}
            />
            <SingleField
              name={<Trans>Email address</Trans>}
              value={quickReferralModalSnap.basicInfoData.email}
            />
            <SingleField
              name={<Trans>Phone number</Trans>}
              value={quickReferralModalSnap.basicInfoData.phone_number}
            />
            <SingleField
              name={<Trans>Date of birth</Trans>}
              value={
                quickReferralModalSnap.basicInfoData.date_of_birth
                  ? djs(quickReferralModalSnap.basicInfoData.date_of_birth).format('DD MMMM YYYY')
                  : ''
              }
            />
            <SingleField
              name={<Trans>USI</Trans>}
              value={quickReferralModalSnap.basicInfoData.universal_student_identifier}
            />
          </SimpleGrid>
        </Box>
        <Box w="full">
          <SummarySectionHeading onEditClick={() => setStep(1)}>
            <Trans>Course</Trans>
          </SummarySectionHeading>
          <SimpleGrid columns={2} w="full">
            <SingleField name={t`Cost`} value={`$${getCoursePrice()}`} />
            <SingleField
              name={quickReferralModalSnap.courseData.identifier}
              value={quickReferralModalSnap.courseData.title}
            />
          </SimpleGrid>
        </Box>
        <Box w="full">
          <SummarySectionHeading onEditClick={() => setStep(3)}>
            <Trans>Enrolment settings</Trans>
          </SummarySectionHeading>
          <SimpleGrid columns={2} w="full">
            <SingleField
              name={<Trans>Start date</Trans>}
              value={djs(quickReferralModalSnap.enrolmentSettingsData.start_at_date).format(
                'dddd, DD MMMM YYYY',
              )}
            />
            <SingleField
              name={<Trans>Purchase order</Trans>}
              value={quickReferralModalSnap.enrolmentSettingsData.external_id}
            />

            <SingleField
              name={<Trans>Contract type</Trans>}
              value={quickReferralModalSnap.enrolmentSettingsData.enrolment_contract_type}
            />
            <SingleField
              name={<Trans>Contract category</Trans>}
              value={quickReferralModalSnap.enrolmentSettingsData.contract_category}
            />
            <SingleField
              name={<Trans>Course Segment</Trans>}
              value={quickReferralModalSnap.enrolmentSettingsData.course_segment}
            />
            <SingleField
              name={<Trans>Site</Trans>}
              value={`${quickReferralModalSnap.enrolmentSettingsData.selected_site.site_name}, ${quickReferralModalSnap.enrolmentSettingsData.selected_site.site_state}`}
            />
            <SingleField
              name={<Trans>Consultant</Trans>}
              value={
                quickReferralModalSnap.enrolmentSettingsData.selected_consultant.consultant_name
              }
            />
            <SingleField
              name={<Trans>Enrolment pace</Trans>}
              value={EnrolmentPace[quickReferralModalSnap.enrolmentSettingsData.work_schedule]}
            />
          </SimpleGrid>
          {courseStartAndEndDates?.courseEndDate ? (
            <Text
              color="fg.emphasis"
              textStyle="sm"
              textAlign="left"
              fontWeight="normal"
              w="full"
              mt={2}
            >
              <Trans>Course is expected to end by</Trans>{' '}
              {courseStartAndEndDates.courseEndDate.long}
            </Text>
          ) : null}
        </Box>
      </FormContentContainer>
      <FormFooterContainer>
        <Button onClick={handleSubmit} isLoading={isSubmitting}>
          {isPendingReview ? (
            <Trans>Submit referral for review</Trans>
          ) : (
            <Trans>Create and refer participant</Trans>
          )}
        </Button>
        <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
          <Trans>Back</Trans>
        </Button>
      </FormFooterContainer>
    </FormMainContainer>
  );
}

function SummarySectionHeading({
  children,
  onEditClick,
}: {
  children: React.ReactNode;
  onEditClick: () => void;
}) {
  return (
    <HStack justifyContent="space-between" w="full">
      <Text fontSize="md" fontWeight="semibold" lineHeight="6" color="fg.emphasis">
        {children}
      </Text>
      <Button variant="ghost" onClick={onEditClick} leftIcon={<PenIcon size="20px" />} size="sm">
        <Trans>Edit</Trans>
      </Button>
    </HStack>
  );
}

enum EnrolmentPace {
  FULL_TIME = 'Full time',
  PART_TIME = 'Part time',
}
